import * as THREE from 'three';
import { VISUAL_SETTINGS } from '../constants.js';

const WEBGL_CONTEXT_ATTRIBUTES = Object.freeze({
  alpha: true,
  antialias: true,
  depth: true,
  failIfMajorPerformanceCaveat: false,
  powerPreference: 'high-performance',
  premultipliedAlpha: true,
  preserveDrawingBuffer: false,
  stencil: false,
  xrCompatible: true,
});

export function createScene(mount) {
  const scene = new THREE.Scene();
  const worldGroup = new THREE.Group();
  worldGroup.name = 'planet-world';
  scene.add(worldGroup);

  const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 200);
  camera.position.set(0, 0.06, 3);
  let pixelRatioCap = VISUAL_SETTINGS.maxPixelRatio;

  const renderer = createRenderer();

  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.02;
  renderer.setClearColor(0x01040b, 1);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, pixelRatioCap));
  renderer.setSize(mount.clientWidth, mount.clientHeight);
  renderer.shadowMap.enabled = false;
  renderer.domElement.style.display = 'block';

  mount.appendChild(renderer.domElement);

  const resize = () => {
    const width = Math.max(mount.clientWidth, 1);
    const height = Math.max(mount.clientHeight, 1);

    camera.aspect = width / height;
    camera.updateProjectionMatrix();

    if (renderer.xr.isPresenting) {
      return;
    }

    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, pixelRatioCap));
    renderer.setSize(width, height, false);
    renderer.domElement.style.width = `${width}px`;
    renderer.domElement.style.height = `${height}px`;
  };

  const setPixelRatioCap = (nextPixelRatioCap = VISUAL_SETTINGS.maxPixelRatio) => {
    pixelRatioCap = nextPixelRatioCap;
    resize();
  };

  resize();

  return { scene, worldGroup, camera, renderer, resize, setPixelRatioCap };
}

function createRenderer() {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('webgl2', WEBGL_CONTEXT_ATTRIBUTES);

  if (context) {
    return new THREE.WebGLRenderer({
      canvas,
      context,
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance',
    });
  }

  return new THREE.WebGLRenderer({
    antialias: true,
    alpha: true,
    powerPreference: 'high-performance',
  });
}
